import React, { useState, useEffect, useMemo, useRef } from "react";
import { LabelWithControl } from "components/common/LabelControl";
import {
  Button,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  DialogTitle,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import {
  PAGE,
  PO_ELEMENTS,
  PO_ITEM_STATUS,
  PO_REASON,
  PO_STATUS,
} from "components/common/CONSTANTS";
import { DataGrid } from "components/DataGrid/DataGrid";
import { POSummaryColumnDefs } from "./POSummaryColumnDefs";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectReasonDialog } from "components/common/SelectReasonDialog";
import { searchPO, searchPOReset } from "redux/actions/searchPOAction";
import { SupplierSiteIdSearchFeild } from "components/common/SupplierSiteIdSearchFeild";
import { encryptString } from "components/common/EncryptDecrypt";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { showErrorMessageByCode } from "services/commonServices";
import { amendPoPatch } from "redux/actions/amendPOAction";
import { RESET_PO_AMEND_PATCH } from "redux/actions/action-type";
import { convertToCamelCase } from "components/common/Utility";
import { getCancelPOStatusToSend } from "components/common/POUtils";

export const POSummary = () => {
  const userType = useSelector((state) => state.user);
  const { accessiblePages } = userType.userType;
  const { locationResponse } = useSelector((state) => state.locations);
  const searchPoReducer = useSelector((state) => state.searchPO);
  const amendPoReducer = useSelector((state) => state.poAmendPatch);

  const [status, setStatus] = useState("");
  const [supplierSiteId, setSupplierSiteId] = useState("");
  const [supplierSiteDisplayName, setSupplierSiteDisplayName] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [poIdentifier, setPOIdentifier] = useState("");
  const [user, setUser] = useState(null);
  const [createDate, setCreateDate] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [orderDate, setOrderDate] = useState(dayjs());
  const [errors, setErrors] = useState(null);
  const [supplierSiteIdError, setSupplierSiteIdError] = useState(null);
  const dispatch = useDispatch();
  const [validDestinationLocations, setValidDestinationLocations] = useState(
    []
  );
  const statusType = [
    { title: "Select Status", value: "" },
    { title: "All", value: "All" },
  ];

  const statusKeys = Object.keys(PO_STATUS);
  const statusValues = statusKeys.map(item => {return PO_STATUS[item]});
  const uniqueStatus = [...new Set(statusValues)];
  uniqueStatus.forEach(status => {
    statusType.push({
      title: convertToCamelCase(status),
      value: status
    })
  });
  const [enableCancelButton, setEnableCancelButton] = useState(false);
  const [enableLifecycleButton, setEnableLifecycleButton] = useState(false);
  const [enableSplitButton, setEnableSplitButton] = useState(false);
  const [enableAmendButton, setEnableAmendButton] = useState(false);
  const [enableConfirmButton, setEnableConfirmButton] = useState(false);
  const navigate = useNavigate();
  const gridRef = useRef();

  const [showDialog, setShowDialog] = useState(false);
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogmessage, setDialogMessage] = useState("");
  const [splitValue, setSplitValue] = useState();
  const [splitValueError, setSplitValueError] = useState();
  const [clickedButton, setClickedButton] = useState("");
  const BUTTON = {
    CANCEL_PO: "cancel_po",
    AMEND: "amend",
  };
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchResultData, setSearchResultData] = useState([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showConfirmSuccessDialog, setShowConfirmSuccessDialog] =
    useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showSearchErrorMessage, setShowSearchErrorMessage] = useState(false);
  const [dataErrorMessage, setDataErrorMessage] = useState("");
  const FETCH_ORDER_LIMIT = 100;
  const [offset, setOffset] = useState(0);
  const [poListToUpdate, setPOListToUpdate] = useState([]);
  const [failedPOUpdate, setFailedPOUpdate] = useState([]);
  const [accessibleButtonlist, setAccessibleButtonlist] = useState([]);
  const [reasonType, setReasonType] = useState(PO_REASON.ORDER_SUB_TYPE.CANCEL);
  const location = useLocation();
  const [selectedReason, setSelectedReason] = useState();

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      headerClass: "header-wrap",
      autoSizeStrategy: { type: "fitCellContents" },
    };
  }, []);

  useEffect(() => {
    const retainSearchCriteria = location.state
      ? location.state.retainSearchCriteria
      : false;

    if (!retainSearchCriteria) {
      localStorage.removeItem("jobId");
      localStorage.removeItem("supplierSiteId");
      localStorage.removeItem("supplierSiteDisplayName");
      localStorage.removeItem("destinationLocation");
      localStorage.removeItem("createDate");
      localStorage.removeItem("orderDate");
      localStorage.removeItem("deliveryDate");
      localStorage.removeItem("user");
      localStorage.removeItem("status");
      localStorage.removeItem("poIdentifier");
    }

    const storedSiteId = localStorage.getItem("supplierSiteId");
    const storedSiteDisplayName = localStorage.getItem("supplierSiteDisplayName");
    const storesdDestination = localStorage.getItem("destinationLocation");
    const cDate = localStorage.getItem("createDate");
    const dDate = localStorage.getItem("deliveryDate");
    const oDate = localStorage.getItem("orderDate");
    const storedUser = localStorage.getItem("user");
    const storedStatus = localStorage.getItem("status");
    const storedPOIdentifier = localStorage.getItem("poIdentifier");
    setSupplierSiteId(storedSiteId ? storedSiteId : "");
    setSupplierSiteDisplayName(storedSiteDisplayName? storedSiteDisplayName : "");
    setDestinationLocation(storesdDestination ? storesdDestination : "");
    setCreateDate(cDate ? dayjs(cDate) : null);
    setDeliveryDate(dDate ? dayjs(dDate) : null);
    setOrderDate(oDate ? dayjs(oDate) : null);
    setStatus(storedStatus ? storedStatus : "");
    setUser(storedUser ? storedUser : "");
    setPOIdentifier(storedPOIdentifier ? storedPOIdentifier : "");

    const validLocations = locationResponse.map(
      (location) => location.locationId
    );
    setValidDestinationLocations(validLocations);
  }, [locationResponse]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (validDestinationLocations && validDestinationLocations.length > 0) {
      const retainSearchCriteria = location.state
        ? location.state.retainSearchCriteria
        : false;

      if (localStorage.getItem("jobId") || retainSearchCriteria) {
        handleSearch(null, true);
      }
      setOffset(0);
      setSupplierSiteIdError(null);
    }
  }, [validDestinationLocations]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAccessibleButtonlist(accessiblePages[PAGE.PO_SUMMARY.key]);
  }, [accessiblePages]);

  const saveSearchCriteria = () => {
    localStorage.removeItem("supplierSiteId");
    localStorage.removeItem("supplierSiteDisplayName");
    localStorage.removeItem("destinationLocation");
    localStorage.removeItem("createDate");
    localStorage.removeItem("orderDate");
    localStorage.removeItem("deliveryDate");
    localStorage.removeItem("user");
    localStorage.removeItem("status");
    localStorage.removeItem("poIdentifier");
    if (supplierSiteId) {
      localStorage.setItem("supplierSiteId", supplierSiteId);
      localStorage.setItem("supplierSiteDisplayName", supplierSiteDisplayName);
    }
    if (destinationLocation) {
      localStorage.setItem("destinationLocation", destinationLocation);
    }
    if (createDate) {
      localStorage.setItem("createDate", createDate);
    }
    if (orderDate) {
      localStorage.setItem("orderDate", orderDate);
    }
    if (deliveryDate) {
      localStorage.setItem("deliveryDate", deliveryDate);
    }
    if (user) {
      localStorage.setItem("user", user);
    }
    if (status) {
      localStorage.setItem("status", status);
    }
    if (poIdentifier) {
      localStorage.setItem("poIdentifier", poIdentifier);
    }
  };

  useEffect(() => {
    if (searchPoReducer.isSearchLoading) {
      setShowSpinner(true);
    } else if (searchPoReducer.isSearchSuccess) {
      setShowSpinner(false);
      const poResponse = searchPoReducer.searchPOResponse.data;
      const poList = poResponse.map((item) => {
        return { ...item, selected: false };
      });
      if (searchResultData.length > 0) {
        setSearchResultData([...searchResultData, ...poList]);
      } else {
        setSearchResultData(poList);
      }
      dispatch(searchPOReset());
    } else if (searchPoReducer.isSearchFail) {
      dispatch(searchPOReset());
      setShowSpinner(false);
      if (searchPoReducer.searchPOResponse.response.status === 404) {
        if (offset <= 0) {
          setDataErrorMessage("No records found");
        }
      } else {
        setDataErrorMessage("");
        const { errTitle, errContent } = showErrorMessageByCode(
          searchPoReducer.searchPOResponse
        );
        dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
      }
    }
  }, [searchPoReducer, dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (amendPoReducer.isPOAmendPatchLoading) {
      setShowSpinner(true);
    } else if (amendPoReducer.isPOAmendPatchError) {
      const res = amendPoReducer.amendPatchError;
      const selectedPos = [...poListToUpdate];
      const index = selectedPos.findIndex(
        (po) => po.extOrderId === res.extOrderId
      );
      setFailedPOUpdate([...failedPOUpdate, selectedPos[index]]);
      selectedPos.splice(index, 1);
      setPOListToUpdate(selectedPos);
      if (selectedPos.length > 0) {
        if (clickedButton === BUTTON.CANCEL_PO) {
          callCancelPoApi(selectedPos[0]);
        } else {
          callConfirmPOApi(selectedPos[0]);
        }
      } else {
        dispatch({ type: RESET_PO_AMEND_PATCH });
        setShowSpinner(false);
        setShowConfirmSuccessDialog(true);
      }
    } else if (amendPoReducer.isPOAmendPatchSuccess) {
      const res = amendPoReducer.patchOrders;
      const selectedPos = [...poListToUpdate];
      const index = selectedPos.findIndex(
        (po) => po.extOrderId === res.extOrderId
      );
      selectedPos.splice(index, 1);
      setPOListToUpdate(selectedPos);
      if (selectedPos.length > 0) {
        if (clickedButton === BUTTON.CANCEL_PO) {
          callCancelPoApi(selectedPos[0]);
        } else {
          callConfirmPOApi(selectedPos[0]);
        }
      } else {
        dispatch({ type: RESET_PO_AMEND_PATCH });
        setShowSpinner(false);
        setShowConfirmSuccessDialog(true);
      }
    }
  }, [amendPoReducer]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let enableCancelButtonStatus = false;
    let enableLifecycleButtonStatus = false;
    let enableSplitButtonStatus = false;
    let enableAmendButtonStatus = false;
    let enableConfirmButtonStatus = false;

    if (selectedRows.length === 1) {
      const status = selectedRows[0].status;

      const isDeliveryDatePast = dayjs(selectedRows[0].deliveryDate).isBefore(
        dayjs(),
        "date"
      );

      const isOrderDateDatePast = dayjs(selectedRows[0].orderDate).isBefore(
        dayjs(),
        "date"
      );
      switch (status) {
        case PO_STATUS.PENDING:
          enableCancelButtonStatus = !isDeliveryDatePast;
          enableLifecycleButtonStatus = true;
          enableSplitButtonStatus = !isDeliveryDatePast;
          enableAmendButtonStatus = !isDeliveryDatePast;
          enableConfirmButtonStatus = !isOrderDateDatePast;        
          break;
        case PO_STATUS.CONFIRMED:
          enableCancelButtonStatus = !isDeliveryDatePast;
          enableLifecycleButtonStatus = true;
          enableAmendButtonStatus = !isDeliveryDatePast;       
          break;
        case PO_STATUS.AMENDED:
          enableCancelButtonStatus = !isDeliveryDatePast;
          enableLifecycleButtonStatus = true;
          enableAmendButtonStatus = !isDeliveryDatePast;        
          break;  
        case PO_STATUS.SOMS_AMENDED:
          enableCancelButtonStatus = !isDeliveryDatePast;
          enableLifecycleButtonStatus = true;
          enableAmendButtonStatus = !isDeliveryDatePast;        
          break;
        case PO_STATUS.DRAFT:
          enableCancelButtonStatus = !isDeliveryDatePast;
          enableLifecycleButtonStatus = true;
          enableAmendButtonStatus = !isDeliveryDatePast;
          enableConfirmButtonStatus = !isOrderDateDatePast;         
          break;
        case PO_STATUS.RAISED:
          enableCancelButtonStatus = !isDeliveryDatePast;
          enableLifecycleButtonStatus = true;
          enableAmendButtonStatus = !isDeliveryDatePast;      
          break;    
        default:
          enableLifecycleButtonStatus = true;
          break;
      }
    } else if (selectedRows.length > 0) {
      enableCancelButtonStatus = true;
      enableConfirmButtonStatus = true;

      selectedRows.forEach((row) => {
        const isDeliveryDatePast = dayjs(row.deliveryDate).isBefore(
          dayjs(),
          "date"
        );

        const isOrderDateDatePast = dayjs(row.orderDate).isBefore(
          dayjs(),
          "date"
        );

        const status = row.status.toLowerCase();

        if (status !== PO_STATUS.PENDING || isOrderDateDatePast) {
          enableConfirmButtonStatus = false;
        }

        if (status !== PO_STATUS.PENDING || isDeliveryDatePast) {
          enableCancelButtonStatus = false;
        }
      });
    }

    setEnableCancelButton(enableCancelButtonStatus);

    setEnableLifecycleButton(enableLifecycleButtonStatus);

    setEnableSplitButton(enableSplitButtonStatus);

    setEnableAmendButton(enableAmendButtonStatus);

    setEnableConfirmButton(enableConfirmButtonStatus);
  }, [selectedRows]);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleDestinationValueChange = (value) => {
    setDestinationLocation(value ? value : "");
  };

  const handlePOIdentifierChange = (value) => {
    setPOIdentifier(value ? value : "");
  };

  const errorMessage = React.useMemo(() => {
    if (errors === "disableFuture") {
      return "Order date cannot be more than today's date";
    }
    if (errors === "shouldDisableDate") {
      return "You can select maximum 21 days before the today's date";
    }
    if (errors === "invalidDate") {
      return "Please enter date in correct format";
    }
  }, [errors]);

  const handleSearch = async (e, isInitialCall) => {
    e?.preventDefault();
    setOffset(0);
    setSelectedRows([]);
    setPOListToUpdate([]);
    setFailedPOUpdate([]);
    setDataErrorMessage("");
    const errors = {};
    setSearchResultData([]);
    setShowConfirmSuccessDialog(false);
    setShowSearchErrorMessage(false);
    setClickedButton("");
    if (!isInitialCall) {
      localStorage.removeItem("jobId");
    }
    const poNos = poIdentifier ? poIdentifier.split(",") : null;
    const userIds = user ? user.split(",") : null;
    if (
      !localStorage.getItem("jobId") &&
      !supplierSiteId &&
      !destinationLocation &&
      !orderDate &&
      !deliveryDate &&
      !createDate &&
      !userIds &&
      !status &&
      !poIdentifier
    ) {
      setShowSearchErrorMessage(true);
    } else {
      if (userIds != null && userIds.length > 7) {
        errors.userText = "Users can have a maximum of 7 entries.";
      }
      if (poNos != null && poNos.length > 7) {
        errors.poIdentifierText =
          "PO Identifiers can have a maximum of 7 entries.";
      }
      if (
        destinationLocation &&
        !validDestinationLocations.includes(parseInt(destinationLocation))
      ) {
        errors.destinationLocationtext = "Invalid destination location";
      }

      if (Object.keys(errors).length <= 0) {
        callSearchPOApi();
      }
    }
    setErrors(errors);
  };

  const callSearchPOApi = async () => {
    saveSearchCriteria();
    let searchCriteria = {};
    const jobId = localStorage.getItem("jobId");
    if (jobId) {
      searchCriteria = {
        jobId: jobId,
      };
    } else {
      const encryptedUserId = user ? await encryptString(user) : null;
      const createdDateVal = createDate
        ? dayjs(createDate).format("YYYY-MM-DD")
        : null;
      const orderDateVal = orderDate
        ? dayjs(orderDate).format("YYYY-MM-DD")
        : null;
      const deliveryDateVal = deliveryDate
        ? dayjs(deliveryDate).format("YYYY-MM-DD")
        : null;

      let searchedExtOrderId = null;
      let searchedOrderId = null;
      if (poIdentifier) {
        const firstDigit = poIdentifier.substring(0, 1);
        if (firstDigit === "1") {
          searchedExtOrderId = poIdentifier;
        } else {
          searchedOrderId = poIdentifier;
        }
      }

      searchCriteria = {
        extOrderId: searchedExtOrderId,
        orderId: searchedOrderId,
        supplierId: supplierSiteId ? supplierSiteId : null,
        shipToLocationId: destinationLocation ? destinationLocation : null,
        orderDate: orderDateVal,
        deliveryDate: deliveryDateVal,
        createdDate: createdDateVal,
        orderStatus: status ? (status !== "All" ? status : null) : null,
        userId: encryptedUserId,
      };
    }

    const payload = {
      params: {
        purchaseOrder: searchCriteria,
      },
      limit: FETCH_ORDER_LIMIT,
      offset: offset,
    };
    dispatch(searchPO(payload));
  };

  const disablePastDate = (date) => {
    const day = dayjs().subtract(21, "day");
    return !dayjs(day).isBefore(date);
  };

  const handleCancelButton = () => {
    setClickedButton(BUTTON.CANCEL_PO);
    setReasonType(PO_REASON.ORDER_SUB_TYPE.CANCEL);
    setDialogTitle("Select Manual Purchase Order Reason");
    setDialogMessage("Please select manual purchase order cancellation reason");
    setShowDialog(true);
  };

  const handleLifecycleButton = () => {
    const orderId = selectedRows[0].extOrderId;
    navigate(PAGE.PO_LIFECYCLE.url, {
      state: {
        orderId,
      },
    });
  };

  const handleSplitButton = () => {
    setShowSplitDialog(true);
  };

  const handleSplitValueChange = (value) => {
    setSplitValue(value ? value : "");
  };

  const onConfirmSplitButtonClicked = () => {
    setShowSplitDialog(false);
    const orderId = selectedRows[0].extOrderId;
    navigate(PAGE.SPLIT_PO.url, {
      state: {
        orderId,
        noOfOrders: splitValue,
      },
    });
  };

  const cancelSelectedPO = (reason) => {
    setPOListToUpdate([...selectedRows]);
    callCancelPoApi(selectedRows[0], reason);
  };

  const callCancelPoApi = async (po, reason) => {
    const encryptedEmailId = await encryptString(userType.loginResponse?.email);

    const items = po.items.map((item) => {
      return {
        packId: item.packId,
        itemId: item.itemId,
        status: PO_ITEM_STATUS.REMOVE,
      };
    });
    const patchRequestBody = {
      status: getCancelPOStatusToSend(po.status),
      reasonCode: reason ? reason : selectedReason,
      items: items,
      userId: encryptedEmailId,
    };
    dispatch(amendPoPatch(patchRequestBody, po.extOrderId));
  };

  const onConfirmPODialogButtonClicked = () => {
    setShowConfirmationDialog(false);
    setPOListToUpdate([...selectedRows]);
    callConfirmPOApi(selectedRows[0]);
  };

  const callConfirmPOApi = async (po) => {
    const encryptedEmailId = await encryptString(userType.loginResponse?.email);

    const items = po.items.map((item) => {
      return {
        packId: item.packId,
        itemId: item.itemId,
        status:
          item.status === PO_ITEM_STATUS.DRAFT
            ? item.quantityOrdered
              ? PO_ITEM_STATUS.CREATE
              : PO_ITEM_STATUS.INACTIVE
            : item.status,
      };
    });

    const patchRequestBody = {
      status: PO_STATUS.CONFIRMED,
      items: items,
      userId: encryptedEmailId,
    };
    dispatch(amendPoPatch(patchRequestBody, po.extOrderId));
  };

  const handleAmendButton = () => {
    if (selectedRows.length > 0) {
      const orderId = selectedRows[0].extOrderId;
      if (
        selectedRows[0].status === PO_STATUS.PENDING ||
        selectedRows[0].status === PO_STATUS.CONFIRMED ||
        selectedRows[0].status === PO_STATUS.DRAFT
      ) {
        navigate(PAGE.AMEND_PO.url, {
          state: {
            orderId,
          },
        });
      } else {
        setClickedButton(BUTTON.AMEND);
        setReasonType(PO_REASON.ORDER_SUB_TYPE.AMEND);
        setDialogTitle("Select Manual Purchase Order Reason");
        setDialogMessage("Please select manual purchase order amend reason");
        setShowDialog(true);
      }
    }
  };

  const onDialogBackButtonClicked = () => {
    setShowDialog(false);
  };

  const onConfirmReasonClicked = (reason) => {
    setSelectedReason(reason);
    switch (clickedButton) {
      case BUTTON.AMEND:
        const orderId = selectedRows[0].extOrderId;
        navigate(PAGE.AMEND_PO.url, {
          state: {
            orderId,
            reason,
          },
        });
        break;
      case BUTTON.CANCEL_PO:
        setShowDialog(false);
        cancelSelectedPO(reason);
        break;
      default:
        setClickedButton("");
        setShowDialog(false);
        break;
    }
  };

  const handleConfirmButton = () => {
    setShowConfirmationDialog(true);
  };

  const onCellValueChanged = (event) => {
    if (event.colDef.field === "selected") {
      const row = event.data;
      if (row.selected) {
        setSelectedRows([...selectedRows, row]);
      } else {
        const filteredList = selectedRows.filter(
          (item) => item.extOrderId !== row.extOrderId
        );
        setSelectedRows(filteredList);
      }
    }
  };

  const onBodyScroll = (params) => {
    var bottomPx = params.api.getVerticalPixelRange().bottom;
    var gridHeight =
      params.api.getDisplayedRowCount() *
      params.api.getSizesForCurrentTheme().rowHeight;
    if (bottomPx >= gridHeight - 100) {
      if (searchResultData.length % FETCH_ORDER_LIMIT === 0) {
        setOffset(searchResultData.length);
      }
    }
  };

  useEffect(() => {
    if (offset > 0) {
      callSearchPOApi();
    }
  }, [offset]); //eslint-disable-line react-hooks/exhaustive-deps

  const onSplitValueChange = (event) => {
    const value = event.target.value.replace(/[^0-9, ]/g, "");
    handleSplitValueChange(value);

    if (parseInt(value) < 2 || parseInt(value) > 9) {
      setSplitValueError(true);
    } else {
      setSplitValueError(false);
    }
  };

  const handleConfirmCancelDalogOkClick = () => {
    dispatch({ type: RESET_PO_AMEND_PATCH });
    setShowConfirmSuccessDialog(false);
    handleSearch(null, true);
  };

  return (
    <>
      <div className="mainPageStyling">
        {showSpinner && (
          <Backdrop
            sx={{
              color: "#fff",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="inherit" />

            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Loading...
            </Typography>
          </Backdrop>
        )}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
            {PAGE.PO_SUMMARY.label}
          </Typography>
        </div>
        <div className="labelControl">
          <Grid
            component="form"
            onSubmit={handleSearch}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
              marginBottom: "10px",
            }}
          >
            <Grid container spacing={2} sx={{ "& > *": { flex: 1 } }}>
              <Grid item xs={12} sm={4} md={3}>
                <Grid container spacing={2} alignItems="flexStart">
                  <Grid item xs={6} sm={6} md={6}>
                    <SupplierSiteIdSearchFeild
                      isRequired={false}
                      existingSupplierSiteId={supplierSiteId}
                      existingSupplierSiteDisplayName={supplierSiteDisplayName}
                      onValueChange={(event, newValue) => {
                        setSupplierSiteId(newValue?.id);
                        setSupplierSiteDisplayName(newValue?.searchOptionToDisplay);
                      }}
                      error={
                        !supplierSiteId
                          ? errors?.supplierSiteIdText ||
                            supplierSiteIdError?.supplierSiteIdText
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <LabelWithControl label="Destination Location">
                      <TextField
                        id="destinationLocation"
                        variant="outlined"
                        size="small"
                        placeholder="Destination Location"
                        helperText={errors?.destinationLocationtext || ""}
                        error={errors?.destinationLocationtext}
                        value={destinationLocation}
                        onChange={(e) =>
                          handleDestinationValueChange(
                            e.target.value.replace(/[^0-9 ]/g, "")
                          )
                        }
                        inputProps={{ maxLength: 32 }}
                      />
                    </LabelWithControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={4} md={1.5}>
                <LabelWithControl label="Create Date">
                  <DatePicker
                    format="DD/MM/YYYY"
                    size="small"
                    value={createDate}
                    onChange={(x) => setCreateDate(x)}
                    shouldDisableDate={disablePastDate}
                    onError={(newError) => setErrors(newError)}
                    maxDate={orderDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        placeholder: "Create Date ",
                        helperText:
                          errors?.createDatetext || "" || errorMessage,
                        inputProps: { 'data-testid': 'create-date-input' },
                      },
                    }}
                  />
                </LabelWithControl>
              </Grid>
              <Grid item xs={12} sm={4} md={1.5}>
                <LabelWithControl label="Order Date">
                  <DatePicker
                    format="DD/MM/YYYY"
                    size="small"
                    // defaultValue={!searchedJobId ? dayjs() : null}
                    value={orderDate}
                    onChange={(x) => setOrderDate(x)}
                    minDate={createDate}
                    maxDate={deliveryDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        placeholder: "Order Date",
                        helperText: errors?.orderDateText || "",
                        error: Boolean(errors?.orderDateText),
                        inputProps: { 'data-testid': 'order-date-input' },
                      },
                    }}
                  />
                </LabelWithControl>
              </Grid>
              <Grid item xs={12} sm={4} md={1.5}>
                <LabelWithControl label="Delivery Date">
                  <DatePicker
                    format="DD/MM/YYYY"
                    size="small"
                    value={deliveryDate}
                    onChange={(x) => setDeliveryDate(x)}
                    minDate={orderDate || dayjs().subtract(14, "day")}
                    slotProps={{
                      textField: {
                        size: "small",
                        placeholder: "Delivery Date",
                        helperText: errors?.deliveryDateText || "",
                        error: Boolean(errors?.deliveryDateText),
                        inputProps: { 'data-testid': 'delivery-date-input' },
                      },
                    }}
                  />
                </LabelWithControl>
              </Grid>
              <Grid item xs={12} sm={4} md={1.5}>
                <LabelWithControl label="User" labelId="user">
                  <TextField
                    id="user"
                    variant="outlined"
                    size="small"
                    placeholder="User"
                    helperText={errors?.userText || ""}
                    error={errors?.userText}
                    value={user}
                    onChange={(x) => setUser(x.target.value)}
                    inputProps={{ maxLength: 100 }}
                  />
                </LabelWithControl>
              </Grid>
              <Grid item xs={12} sm={4} md={1}>
                <LabelWithControl label="Status">
                  <FormControl fullWidth size="small">
                    <Select
                      labelId="status"
                      id="status"
                      displayEmpty
                      value={status || ""}
                      placeholder="Select Status"
                      onChange={handleStatusChange}
                      className="dropDownValue"
                    >
                      {statusType.map((x) => (
                        <MenuItem key={x.value} value={x.value}>
                          {x.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </LabelWithControl>
              </Grid>
              <Grid item xs={12} sm={4} md={1}>
                <LabelWithControl label="PO Identifier" labelId="poIdentifier">
                  <TextField
                    id="poIdentifier"
                    variant="outlined"
                    size="small"
                    placeholder="PO Identifier"
                    helperText={errors?.poIdentifierText || ""}
                    error={errors?.poIdentifierText}
                    value={poIdentifier}
                    onChange={(x) =>
                      handlePOIdentifierChange(
                        x.target.value.replace(/[^0-9 ]/g, "")
                      )
                    }
                    inputProps={{ maxLength: 10 }}
                  />
                </LabelWithControl>
              </Grid>
              <Grid item xs={12} sm={4} md={1}>
                <LabelWithControl
                  labelVisible="hidden"
                  label="Order ID"
                  labelId="orderId"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      width: "-webkit-fill-available",
                      height: "25px",
                      fontSize: "12px",
                    }}
                    type="submit"
                    fullWidth
                  >
                    Search
                  </Button>
                </LabelWithControl>
              </Grid>
            </Grid>
          </Grid>
          {showSearchErrorMessage && (
            <div
              style={{
                fontWeight: "bold",
                fontSize: "14px",
                color: "red",
                marginBottom: "10px",
              }}
            >
              Please select at least 1 search field
            </div>
          )}

          {searchResultData.length > 0 ? (
            <div style={{ width: "100%", height: "calc( 100vh - 240px)" }}>
              <DataGrid
                columnDefs={POSummaryColumnDefs}
                rowData={searchResultData}
                defaultColDef={defaultColDef}
                onCellValueChanged={onCellValueChanged}
                onBodyScroll={onBodyScroll}
                gridRef={gridRef}
              />
            </div>
          ) : (
            <div
              style={{
                fontWeight: "bold",
                fontSize: "14px",
                color: "red",
                marginBottom: "10px",
              }}
            >
              {dataErrorMessage}
            </div>
          )}
        </div>
      </div>

      {searchResultData.length > 0 && (
        <>
          <Grid container spacing={1}>
            {/* Left side buttons */}
            <Grid item xs={6} container justifyContent="flex-start" spacing={2}>
              <Grid item>
                <div className="parentPoButtons">
                  {accessibleButtonlist.includes(
                    PO_ELEMENTS.PO_SUMMARY.CANCEL_BUTTON
                  ) && (
                    <Button
                      className="commonPOButtons"
                      variant="contained"
                      color="primary"
                      disabled={!enableCancelButton}
                      onClick={handleCancelButton}
                    >
                      Cancel PO
                    </Button>
                  )}
                </div>
              </Grid>
              <Grid item>
                <div className="parentPoButtons">
                  {accessibleButtonlist.includes(
                    PO_ELEMENTS.PO_SUMMARY.LIFECYCLE_BUTTON
                  ) && (
                    <Button
                      className="commonPOButtons"
                      variant="contained"
                      color="primary"
                      disabled={!enableLifecycleButton}
                      onClick={handleLifecycleButton}
                    >
                      Lifecycle
                    </Button>
                  )}
                </div>
              </Grid>
            </Grid>

            {/* Right side buttons */}
            <Grid item xs={6} container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <div className="parentPoButtons">
                  {accessibleButtonlist.includes(
                    PO_ELEMENTS.PO_SUMMARY.SPLIT_BUTTON
                  ) && (
                    <Button
                      className="commonPOButtons"
                      variant="contained"
                      color="primary"
                      disabled={!enableSplitButton}
                      onClick={handleSplitButton}
                    >
                      Split
                    </Button>
                  )}
                </div>
              </Grid>
              <Grid item>
                <div className="parentPoButtons">
                  {accessibleButtonlist.includes(
                    PO_ELEMENTS.PO_SUMMARY.AMEND_BUTTON
                  ) && (
                    <Button
                      className="commonPOButtons"
                      variant="contained"
                      color="primary"
                      disabled={!enableAmendButton}
                      onClick={handleAmendButton}
                    >
                      Amend
                    </Button>
                  )}
                </div>
              </Grid>
              <Grid item>
                <div className="parentPoButtons">
                  {accessibleButtonlist.includes(
                    PO_ELEMENTS.PO_SUMMARY.CONFIRM_BUTTON
                  ) && (
                    <Button
                      className="commonPOButtons"
                      variant="contained"
                      color="primary"
                      disabled={!enableConfirmButton}
                      onClick={handleConfirmButton}
                    >
                      Confirm
                    </Button>
                  )}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      <SelectReasonDialog
        reasonType={reasonType}
        title={dialogTitle}
        message={dialogmessage}
        selectReasonDialog={showDialog}
        onReasonDialogClose={onDialogBackButtonClicked}
        handleConfirmReason={onConfirmReasonClicked}
      />

      <Dialog open={showConfirmationDialog}>
        <div className="dialogStyling">
          <DialogTitle>Confirm Purchase Order</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to confirm Pending Purchase Order?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowConfirmationDialog(false)}
              className="commonPOButtons"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onConfirmPODialogButtonClicked()}
              className="commonPOButtons"
            >
              Confirm
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      <Dialog
        open={showConfirmSuccessDialog}
        onClose={handleConfirmCancelDalogOkClick}
      >
        <DialogTitle>
          {clickedButton === BUTTON.CANCEL_PO ? "Cancel" : "Confirm"} Purchase
          Orders
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div className="totalOrdersCreated">
              {" "}
              Total Purchase Orders: {selectedRows.length}
            </div>
            {failedPOUpdate.length > 0 && (
              <div className="totalOrdersCreated">
                Failed Purchase Orders: {failedPOUpdate.length}
              </div>
            )}
            <div className="totalOrdersCreated">
              Purchase Orders{" "}
              {clickedButton === BUTTON.CANCEL_PO ? "Cancelled" : "Confirmed"}{" "}
              Successfully: {selectedRows.length - failedPOUpdate.length}
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancelDalogOkClick} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showSplitDialog}>
        <div className="dialogStyling">
          <DialogTitle>Split PO</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please enter the number of orders to be created between 2-9
            </DialogContentText>
            <div className="labelControl">
              <LabelWithControl label="Number of Orders:" labelId="noOfOrders">
                <TextField
                  labelid="noOfOrders"
                  variant="outlined"
                  size="small"
                  placeholder=""
                  value={splitValue}
                  onChange={onSplitValueChange}
                  inputProps={{ maxLength: 1 }}
                  error={splitValueError}
                  helperText={
                    splitValueError ? "Value must be between 2 - 9" : ""
                  }
                />
              </LabelWithControl>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setShowSplitDialog(false);
                setSplitValue();
              }}
              className="commonPOButtons"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onConfirmSplitButtonClicked()}
              className="commonPOButtons"
              disabled={!splitValue || splitValueError}
            >
              Confirm
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </>
  );
};
